import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PropertyFormData, PropertyPhoto, ListingsByLanguage, DescriptionLanguage, UserProfile } from '@/types/property';
import { EnhancedHeroSection } from '@/components/EnhancedHeroSection';
import { UnifiedListingChat } from '@/components/UnifiedListingChat';
import { ListingResults } from '@/components/ListingResults';
import { ListingScoreCard } from '@/components/ListingScoreCard';
import { Layout } from '@/components/layout';
import { Sparkles, Home, User, LogOut, LayoutList } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { NotificationBell } from '@/components/NotificationBell';
import { toast } from '@/hooks/use-toast';
import { generateListingWithAI, generateFallbackListing } from '@/lib/generateListing';
import { generatePropertyProfilePicture, generatePropertyVideo } from '@/lib/generatePropertyMedia';
import {
  generateCinematicPropertyVideo,
  generateSingleClip,
  loadListingClips,
  orderCinematicPhotos,
  stitchClipsToMp4,
  type CinematicClip,
} from '@/lib/generateCinematicVideo';
import { enhancePhotosWithAI } from '@/lib/enhancePhotos';
import { fullStageEmptyRooms } from '@/lib/fullStagePhotos';
import { checkReeditQuota, consumeReeditQuota } from '@/lib/reeditQuota';
import { generatePremiumDescription } from '@/lib/generatePremiumDescription';
import { shouldMarkListingAsTrial } from '@/lib/trialUtils';
import { hasPremiumDescriptionAccess } from '@/types/billing';
import { useAuth } from '@/hooks/useAuth';
import { useBilling } from '@/hooks/useBilling';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { getDataFastAttribution } from '@/lib/datafast';
import { uploadMediaToStorage, uploadAllPhotosToStorage } from '@/lib/uploadMedia';
import { UpgradePrompt, UpgradeBanner } from '@/components/UpgradePrompt';
import { useCreateListing } from '@/contexts/CreateListingContext';
import { WelcomeTour } from '@/components/WelcomeTour';
import { PageMeta } from '@/components/PageMeta';

type Step = 'hero' | 'create' | 'results';
type GenerationPhase = 'enhancing' | 'generating-description' | 'generating-media' | 'uploading-photos' | 'complete';

const initialFormData: PropertyFormData = {
  listingType: 'sale',
  category: 'residential',
  type: '',
  bedrooms: 1,
  bathrooms: 1,
  squareMeters: '',
  price: '',
  address: '',
  city: '',
  features: [],
  condition: '',
  highlights: '',
  languages: ['en'],
  furnished: 'unfurnished',
  useEmojis: true,
  includeAgentInfo: true,
};

// Helper function to create form data with profile defaults applied
function getFormDataWithProfileDefaults(profile: UserProfile | null): PropertyFormData {
  return {
    ...initialFormData,
    // Apply default languages from profile
    languages: (profile?.default_languages as DescriptionLanguage[]) || initialFormData.languages,
    // Apply emoji and agent info defaults
    useEmojis: profile?.use_emojis ?? initialFormData.useEmojis,
    includeAgentInfo: profile?.include_agent_info ?? initialFormData.includeAgentInfo,
    // Inherit user's market so the chat tailors fields out of the box
    market: (profile as any)?.market || 'generic',
    marketAttributes: {},
  };
}

export default function Index() {
  const [currentStep, setCurrentStep] = useState<Step>('hero');
  const [formData, setFormData] = useState<PropertyFormData>(initialFormData);
  const [photos, setPhotos] = useState<PropertyPhoto[]>([]);
  const [listingsByLanguage, setListingsByLanguage] = useState<ListingsByLanguage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>('enhancing');
  const [isSaving, setIsSaving] = useState(false);
  const [savedListingId, setSavedListingId] = useState<string | null>(null);
  const [processingPhotoId, setProcessingPhotoId] = useState<string>();
  const [processedCount, setProcessedCount] = useState(0);
  const [autoSaveOnComplete, setAutoSaveOnComplete] = useState(false);
  const [sendEmailOnComplete, setSendEmailOnComplete] = useState(false);
  const [reEnhancingPhotoId, setReEnhancingPhotoId] = useState<string>();
  const [reEnhancedPhotoIds, setReEnhancedPhotoIds] = useState<Set<string>>(new Set());
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [mediaGenerationStatus, setMediaGenerationStatus] = useState<{
    profilePicture: 'idle' | 'generating' | 'complete' | 'error';
    video: 'idle' | 'generating' | 'complete' | 'error';
  }>({ profilePicture: 'idle', video: 'idle' });
  const [mediaBlobs, setMediaBlobs] = useState<{ collage?: Blob; video?: Blob }>({});
  // Cinematic AI Video add-on state (per-listing)
  const [cinematicAddon, setCinematicAddon] = useState<{
    purchased: boolean;
    used: number;
    paid: number;
    isTrial: boolean;
    resolution: '720p' | '1080p';
    clipRegensUsed: number;
    clipRegensAllowance: number;
  }>({ purchased: false, used: 0, paid: 0, isTrial: false, resolution: '720p', clipRegensUsed: 0, clipRegensAllowance: 4 });
  const [cinematicClips, setCinematicClips] = useState<CinematicClip[]>([]);
  const [regeneratingClipIndex, setRegeneratingClipIndex] = useState<number | null>(null);
  const [cinematicProgress, setCinematicProgress] = useState<{ done: number; total: number; stage: 'idle' | 'clips' | 'stitching' }>({ done: 0, total: 0, stage: 'idle' });
  const [isGeneratingCinematic, setIsGeneratingCinematic] = useState(false);
  const [cinematicStyle, setCinematicStyle] = useState<'walkthrough' | 'drone'>('walkthrough');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeLimitType, setUpgradeLimitType] = useState<'listings' | 'photoEdits' | 'virtualStaging' | 'aiAdditions'>('listings');
  const [showStopDialog, setShowStopDialog] = useState(false);
  const enhancementAbortRef = useRef<AbortController | null>(null);
  const { user, profile, signOut, isLoading: authLoading } = useAuth();
  const { billing, canCreateNewListing, tierConfig, checkFeature, refreshBilling } = useBilling();
  const { shouldOpenCreate, resetCreate, consumePendingDemoPhoto } = useCreateListing();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // If the user unlocked a photo in the homepage demo widget, seed the create
  // flow with it (already enhanced — it won't consume another photo edit).
  const consumeDemoPhotos = useCallback((): PropertyPhoto[] => {
    const demo = consumePendingDemoPhoto();
    if (!demo) return [];
    return [{
      id: crypto.randomUUID(),
      preview: demo.originalUrl || demo.enhancedUrl,
      enhanced: demo.enhancedUrl,
      activeVersion: 'enhanced',
      originalMissing: !demo.originalUrl,
    }];
  }, [consumePendingDemoPhoto]);

  // Check for create=true query parameter to start listing creation flow
  useEffect(() => {
    if (searchParams.get('create') !== 'true' || authLoading) return;

    if (user) {
      setFormData(getFormDataWithProfileDefaults(profile));
      setPhotos(consumeDemoPhotos());
      setListingsByLanguage([]);
      setSavedListingId(null);
      setProfilePictureUrl(null);
      setVideoUrl(null);
      setMediaGenerationStatus({ profilePicture: 'idle', video: 'idle' });
      setCurrentStep('create');
      setSearchParams({}, { replace: true });
    } else {
      toast({
        title: "Login required",
        description: "Please login or create an account to create listings.",
      });
      navigate('/auth');
    }
  }, [searchParams, user, authLoading, profile, setSearchParams, navigate, consumeDemoPhotos]);

  // Listen for create trigger from context (when clicking Create Listing button on homepage)
  useEffect(() => {
    // Wait for auth to finish loading before acting on the trigger
    if (!shouldOpenCreate || authLoading) return;

    if (user) {
      // Reset form data and start fresh listing creation with profile defaults applied
      setFormData(getFormDataWithProfileDefaults(profile));
      setPhotos(consumeDemoPhotos());
      setListingsByLanguage([]);
      setSavedListingId(null);
      setProfilePictureUrl(null);
      setVideoUrl(null);
      setMediaGenerationStatus({ profilePicture: 'idle', video: 'idle' });
      setCurrentStep('create');
      resetCreate();
    } else {
      toast({
        title: "Login required",
        description: "Please login or create an account to create listings.",
      });
      navigate('/auth');
      resetCreate();
    }
  }, [shouldOpenCreate, user, authLoading, profile, resetCreate, navigate, consumeDemoPhotos]);

  // Apply profile defaults when profile is loaded (only on initial load when form is in default state)
  useEffect(() => {
    if (profile && currentStep === 'hero') {
      // Only apply profile defaults on initial load (hero step)
      // This prevents overwriting user changes when profile loads after they've started editing
      setFormData(getFormDataWithProfileDefaults(profile));
    }
  }, [profile, currentStep]);

  // Auto-save listing and send notifications when background processing is enabled
  const autoSaveAndNotify = async (
    generatedListings: ListingsByLanguage[], 
    enhancedPhotos: PropertyPhoto[],
    profilePicUrl?: string | null,
    vidUrl?: string | null,
    collageBlob?: Blob,
    collageImageBlob?: Blob,  // Image preview blob for display
    videoBlob?: Blob,
    shouldSendEmail?: boolean
  ) => {
    if (!user) return null;
    
    setIsSaving(true);
    setGenerationPhase('uploading-photos');
    
    const primaryListings = generatedListings[0]?.listings || [];
    const facebookListing = primaryListings.find(l => l.platform === 'facebook');
    const generalListing = primaryListings.find(l => l.platform === 'general');
    const instagramListing = primaryListings.find(l => l.platform === 'instagram');
    const instagramStory = primaryListings.find(l => l.platform === 'instagram-story');

    // Generate a temporary listing ID for photo uploads
    const tempListingId = crypto.randomUUID();

    // Upload all photos to storage BEFORE saving to database
    // This ensures we store permanent URLs, not blob URLs
    console.log('📤 Uploading photos to storage before saving...');
    const { originalUrls, enhancedUrls } = await uploadAllPhotosToStorage(
      enhancedPhotos.map(p => ({ preview: p.preview, enhanced: p.enhanced })),
      user.id,
      tempListingId,
      (current, total) => {
        console.log(`📸 Uploading photo ${current}/${total}`);
      }
    );
    console.log('✅ Photos uploaded to storage');

    // First insert listing with uploaded photo URLs
    const { data, error } = await supabase.from('listings').insert({
      user_id: user.id,
      listing_type: formData.listingType,
      property_type: formData.type,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      square_meters: formData.squareMeters ? parseInt(formData.squareMeters) : null,
      price: parseInt(formData.price),
      address: formData.address,
      city: formData.city,
      features: formData.features,
      condition: formData.condition,
      highlights: formData.highlights,
      language: formData.languages.join(','),
      // Use the uploaded storage URLs instead of blob URLs
      photos: originalUrls,
      enhanced_photos: enhancedUrls,
      // Store per-photo enhancement options for re-enhancement
      photo_enhancement_options: enhancedPhotos.map(p => p.enhancementOptions || { addDecorations: false, goldenHourMode: false }),
      facebook_title: facebookListing?.title,
      facebook_description: facebookListing?.description,
      general_title: generalListing?.title,
      general_description: generalListing?.description,
      instagram_post: instagramListing?.description,
      instagram_story: instagramStory?.description,
      content_by_language: JSON.parse(JSON.stringify(generatedListings)),
      is_trial: shouldMarkListingAsTrial(billing),
      profile_picture_url: null, // Will be updated after storage upload
      video_url: null, // Will be updated after storage upload
    } as any).select().single();

    if (error) {
      console.error('Error auto-saving listing:', error);
      toast({
        title: "Auto-save failed",
        description: error.message,
        variant: "destructive",
      });
      setIsSaving(false);
      setAutoSaveOnComplete(false);
      return null;
    } else {
      // Store the listing ID for QR code generation and preview
      if (data?.id) {
        setSavedListingId(data.id);
        
        // Upload media blobs to storage and update listing with permanent URLs
        const mediaUpdates: { profile_picture_url?: string; video_url?: string } = {};
        
        // Upload image preview (or fallback to PDF) for display
        const imageToUpload = collageImageBlob || collageBlob;
        if (imageToUpload) {
          console.log('📤 Uploading flyer image preview to storage...');
          const collageStorageUrl = await uploadMediaToStorage(imageToUpload, user.id, data.id, 'collage');
          if (collageStorageUrl) {
            mediaUpdates.profile_picture_url = collageStorageUrl;
            setProfilePictureUrl(collageStorageUrl); // Update state with permanent URL
          }
        }
        
        if (videoBlob) {
          console.log('📤 Uploading video to storage...');
          const videoStorageUrl = await uploadMediaToStorage(videoBlob, user.id, data.id, 'video');
          if (videoStorageUrl) {
            mediaUpdates.video_url = videoStorageUrl;
            setVideoUrl(videoStorageUrl); // Update state with permanent URL
          }
        }
        
        // Update listing with permanent media URLs
        if (Object.keys(mediaUpdates).length > 0) {
          await supabase.from('listings').update(mediaUpdates).eq('id', data.id);
          console.log('✅ Media URLs updated in database');
        }
      }
      
      toast({
        title: "Listing saved!",
        description: "Your listing has been created and saved automatically.",
      });

      // Create and upload thumbnail
      if (data?.id && enhancedPhotos.length > 0) {
        try {
          const { createAndUploadThumbnail } = await import('@/lib/createThumbnail');
          const firstPhoto = enhancedPhotos[0];
          const imageSource = firstPhoto.enhanced || firstPhoto.preview;
          
          if (imageSource && !imageSource.startsWith('blob:')) {
            const thumbnailUrl = await createAndUploadThumbnail(imageSource, data.id, user.id);
            if (thumbnailUrl) {
              await supabase.from('listings').update({ thumbnail_url: thumbnailUrl }).eq('id', data.id);
            }
          }
        } catch (thumbError) {
          console.error('Failed to create thumbnail:', thumbError);
        }
      }

      // Always send in-app notification; only include email if user opted in
      if (data?.id && user.id) {
        try {
          const listingTitle = generalListing?.title || facebookListing?.title || `${formData.type} in ${formData.city}`;
          const emailOptIn = shouldSendEmail ?? sendEmailOnComplete;
          console.log('Sending notification (email opt-in:', emailOptIn, ')');
          const notifyResult = await supabase.functions.invoke('send-notification', {
            body: {
              // Only include email address if user opted in for email notification
              to: emailOptIn ? user.email : undefined,
              userId: user.id,
              listingId: data.id,
              listingTitle,
              status: 'complete',
            },
          });
          console.log('Notification result:', notifyResult);
          
          if (notifyResult.error) {
            console.error('Notification error:', notifyResult.error);
          }
        } catch (notifyError) {
          console.error('Failed to send notification:', notifyError);
        }
      }
    }
    
    setIsSaving(false);
    setAutoSaveOnComplete(false);
    return data?.id ?? null;
  };
  // Handle stop enhancement request
  const handleStopEnhancement = useCallback(() => {
    if (enhancementAbortRef.current) {
      enhancementAbortRef.current.abort();
      console.log('⏹️ Enhancement abort signal sent');
    }
  }, []);

  // Handle keep/discard choice after stopping enhancement
  const handleStopDialogChoice = useCallback((keepEnhanced: boolean) => {
    setShowStopDialog(false);
    
    if (!keepEnhanced) {
      // Discard all enhancements - revert to originals
      const revertedPhotos = photos.map(photo => ({
        ...photo,
        enhanced: photo.preview, // Use original as "enhanced"
      }));
      setPhotos(revertedPhotos);
      toast({
        title: "Enhancement stopped",
        description: "All photos reverted to originals.",
      });
    } else {
      // Keep what we have - enhanced ones stay, unprocessed use originals
      toast({
        title: "Enhancement stopped",
        description: `Kept ${processedCount} enhanced photo${processedCount !== 1 ? 's' : ''}. Remaining photos will use originals.`,
      });
    }
    
    // Reset generation state
    setIsGenerating(false);
    setGenerationPhase('complete');
    setProcessingPhotoId(undefined);
    setProcessedCount(0);
    enhancementAbortRef.current = null;
    
    // Go back to the create step so user can adjust and retry
    setCurrentStep('create');
  }, [photos, processedCount]);

  const generateListings = async (photosToEnhance?: PropertyPhoto[], shouldSendEmail?: boolean) => {
    // Use passed photos or fall back to state (for regenerate calls)
    const photosForProcessing = photosToEnhance || photos;

    // Photos that arrived already enhanced (e.g. carried over from the homepage
    // demo widget) are skipped by the enhancement step and don't consume edits.
    const photosNeedingEnhancement = photosForProcessing.filter(p => !p.enhanced);

    // Track email notification preference
    setSendEmailOnComplete(!!shouldSendEmail);
    
    // Check if user can create a new listing (for logged in users)
    if (user && billing) {
      if (!canCreateNewListing) {
        // Show upgrade prompt with positive messaging
        setUpgradeLimitType('listings');
        setShowUpgradePrompt(true);
        return;
      }
      
      // Photo enhancements, virtual staging (decorative + full), golden hour,
      // sky/lawn/reflection cleanup and other enhancement options are all
      // unlimited — users get as many enhancements as they have photos/
      // properties for. Only re-edits consume a monthly pool (enforced on the
      // re-edit action itself, not here).
    }
    
    setIsGenerating(true);
    setGenerationPhase('enhancing');
    setProcessedCount(0);
    
    // Create a fresh AbortController for this enhancement run
    const abortController = new AbortController();
    enhancementAbortRef.current = abortController;
    
    try {
      // Step 1: Enhance photos with AI (if they don't already have enhanced versions)
      console.log('Checking photos for enhancement...');
      
      // Only photos without an enhanced version go through enhancement — ones
      // carried in pre-enhanced (demo unlock) or from a previous run are kept.
      let enhancedPhotos: PropertyPhoto[];

      // Merge freshly-enhanced results back into the full photo list, preserving order
      const mergeEnhanced = (enhanced: PropertyPhoto[]): PropertyPhoto[] => {
        const enhancedById = new Map(enhanced.map(p => [p.id, p]));
        return photosForProcessing.map(p => enhancedById.get(p.id) ?? p);
      };

      if (photosNeedingEnhancement.length === 0) {
        console.log('Photos already have enhanced versions, skipping enhancement');
        enhancedPhotos = photosForProcessing;
        setPhotos(enhancedPhotos);
      } else {
        console.log(`Enhancing ${photosNeedingEnhancement.length}/${photosForProcessing.length} photos with AI...`);
        console.log('Photos have enhancement options:', photosNeedingEnhancement[0]?.enhancementOptions);

        try {
          const result = await enhancePhotosWithAI(photosNeedingEnhancement, (enhanced, total, photoId) => {
            setProcessedCount(enhanced);
            setProcessingPhotoId(photoId);
          }, profile, abortController.signal);

          // If aborted, show the keep/discard dialog
          if (result.aborted) {
            console.log(`Enhancement aborted: ${result.successCount}/${result.totalCount} completed`);
            setShowStopDialog(true);
            // Store the partial results for the dialog to use
            enhancedPhotos = mergeEnhanced(result.photos);
            setPhotos(enhancedPhotos);
            // Stop the generation flow here - the dialog will handle next steps
            return;
          }

          enhancedPhotos = mergeEnhanced(result.photos);
          setPhotos(enhancedPhotos);
          
          // Provide detailed feedback on enhancement results
          if (result.failedIndices.length === 0) {
            console.log('All photos enhanced successfully');
          } else if (result.partialSuccess) {
            console.warn(`${result.successCount}/${result.totalCount} photos enhanced`);
          } else {
            console.warn('Photo enhancement fully failed');
          }
        } catch (enhanceError) {
          console.error('Photo enhancement failed:', enhanceError);
          // Continue with original photos if enhancement fails (keep any
          // photos that were already enhanced before this run)
          enhancedPhotos = photosForProcessing.map(photo => ({
            ...photo,
            enhanced: photo.enhanced || photo.preview,
          }));
          setPhotos(enhancedPhotos);
        }
      }

      // Step 1b: Full Virtual Staging for photos marked as empty rooms.
      // This must run in the create-page flow too; otherwise selected rooms
      // proceed to save with only the standard enhanced, unfurnished output.
      const anyFullStaging = enhancedPhotos.some(p => p.enhancementOptions?.fullStaging);
      if (anyFullStaging) {
        const stagingResult = await fullStageEmptyRooms(enhancedPhotos);

        if (stagingResult.errorMessage || stagingResult.successCount === 0) {
          setPhotos(enhancedPhotos);
          toast({
            title: 'Full virtual staging failed',
            description: stagingResult.errorMessage || 'No furnished image was produced. Your listing was not saved with unfurnished staged photos.',
            variant: 'destructive',
          });
          return;
        }

        enhancedPhotos = stagingResult.photos;
        setPhotos(enhancedPhotos);
      }

      // Step 2: Generate listings with AI (standard + premium in parallel)
      setGenerationPhase('generating-description');
      console.log('Generating listings with AI for', formData.languages.length, 'languages...');
      
      const shouldGeneratePremium = billing && hasPremiumDescriptionAccess(billing);
      
      const descriptionPromises: [Promise<ListingsByLanguage[]>, Promise<Record<string, string> | null>] = [
        generateListingWithAI(formData, enhancedPhotos, profile),
        shouldGeneratePremium
          ? generatePremiumDescription(formData, enhancedPhotos, profile).catch(err => {
              console.warn('Premium description failed (non-blocking):', err);
              return null;
            })
          : Promise.resolve(null),
      ];

      const [generatedListings, premiumDescriptions] = await Promise.all(descriptionPromises);

      // Merge premium descriptions into the listings
      if (premiumDescriptions) {
        for (const langGroup of generatedListings) {
          const premiumText = premiumDescriptions[langGroup.language];
          if (premiumText) {
            langGroup.listings.push({
              platform: 'premium',
              title: '',
              description: premiumText,
            });
          }
        }
      }

      setListingsByLanguage(generatedListings);

      // Step 3: Generate media (profile picture and video) if enabled
      const firstPhotoOptions = enhancedPhotos[0]?.enhancementOptions;
      const shouldGenerateProfilePicture = firstPhotoOptions?.generateProfilePicture;
      const shouldGenerateVideo = firstPhotoOptions?.generateVideo;
      
      let generatedProfilePictureUrl: string | null = null;
      let generatedVideoUrl: string | null = null;
      let collageBlob: Blob | undefined;
      let collageImageBlob: Blob | undefined;  // Image preview blob for display
      let videoBlob: Blob | undefined;
      
      if (shouldGenerateProfilePicture || shouldGenerateVideo) {
        setGenerationPhase('generating-media');
        const enhancedPhotoUrls = enhancedPhotos
          .map(p => p.enhanced)
          .filter((url): url is string => !!url);
        
        // Build photos with room types for intelligent video ordering
        const photosWithRoomTypes = enhancedPhotos
          .filter(p => p.enhanced)
          .map(p => ({
            url: p.enhanced!,
            roomType: p.roomType || 'other' as const,
          }));
        
        // Generate profile picture and video in parallel if both are enabled
        const mediaPromises: Promise<void>[] = [];
        
        if (shouldGenerateProfilePicture) {
          setMediaGenerationStatus(prev => ({ ...prev, profilePicture: 'generating' }));
          // Pass full property info for the PDF flyer
          const propertyInfo = {
            propertyType: formData.type,
            city: formData.city,
            price: formData.price,
            bedrooms: formData.bedrooms,
            bathrooms: formData.bathrooms,
            squareMeters: formData.squareMeters,
            listingType: formData.listingType,
            address: formData.address,
            features: formData.features,
          };
          mediaPromises.push(
            generatePropertyProfilePicture(enhancedPhotoUrls, formData.type, formData.city, 'modern', propertyInfo)
              .then(result => {
                if (result.success && result.pdfUrl) {
                  // Use image preview URL for display (if available), otherwise fall back to PDF URL
                  generatedProfilePictureUrl = result.imagePreviewUrl || result.pdfUrl;
                  collageBlob = result.blob;  // PDF blob for download
                  collageImageBlob = result.imageBlob;  // Image blob for preview display
                  // Use image preview for display in the UI
                  setProfilePictureUrl(result.imagePreviewUrl || result.pdfUrl);
                  setMediaBlobs(prev => ({ ...prev, collage: result.imageBlob || result.blob }));
                  setMediaGenerationStatus(prev => ({ ...prev, profilePicture: 'complete' }));
                } else {
                  console.error('PDF flyer generation failed:', result.error);
                  setMediaGenerationStatus(prev => ({ ...prev, profilePicture: 'error' }));
                }
              })
          );
        }
        
        if (shouldGenerateVideo) {
          setMediaGenerationStatus(prev => ({ ...prev, video: 'generating' }));
          // Pass photos with room types for intelligent sorting (Exterior -> Living -> Kitchen -> Bedrooms -> Exterior)
          mediaPromises.push(
            generatePropertyVideo(photosWithRoomTypes, formData.type, formData.city)
              .then(result => {
                if (result.success && result.videoUrl) {
                  generatedVideoUrl = result.videoUrl;
                  videoBlob = result.blob;
                  setVideoUrl(result.videoUrl);
                  setMediaBlobs(prev => ({ ...prev, video: result.blob }));
                  setMediaGenerationStatus(prev => ({ ...prev, video: 'complete' }));
                } else {
                  console.error('Video generation failed:', result.error);
                  setMediaGenerationStatus(prev => ({ ...prev, video: 'error' }));
                  toast({
                    title: 'Slideshow video failed',
                    description: result.error
                      ? `${result.error}. You can retry it from the listing page.`
                      : 'You can retry it from the listing page.',
                    variant: 'destructive',
                  });
                }

              })
          );
        }
        
        // Wait for all media generation to complete
        await Promise.allSettled(mediaPromises);
      }
      
      setGenerationPhase('complete');
      setCurrentStep('results');
      
      // Always auto-save if user is logged in
      if (user) {
        toast({
          title: "Listings Generated!",
          description: "Saving your listing...",
        });
        // Auto-save with the generated listings, enhanced photos, and media blobs for storage upload
        const newListingId = await autoSaveAndNotify(generatedListings, enhancedPhotos, generatedProfilePictureUrl, generatedVideoUrl, collageBlob, collageImageBlob, videoBlob, !!shouldSendEmail);
        // Redirect to the dedicated listing URL so the results page has its own shareable/refreshable address.
        if (newListingId) {
          setCurrentStep('create'); // reset so back button lands on the create flow, not stale results
          navigate(`/listing/${newListingId}`);
        }
      } else {
        toast({
          title: "Listings Generated!",
          description: `AI enhanced photos and created descriptions in ${generatedListings.length} language${generatedListings.length > 1 ? 's' : ''}. Login to save.`,
        });
      }
    } catch (error) {
      console.error('AI generation failed:', error);
      
      toast({
        title: "AI Generation Issue",
        description: error instanceof Error ? error.message : "Using fallback generator",
        variant: "destructive",
      });
      
      const fallbackListings = generateFallbackListing(formData, profile);
      const processedPhotos = photosForProcessing.map(photo => ({
        ...photo,
        enhanced: photo.preview,
      }));
      setPhotos(processedPhotos);
      setListingsByLanguage(fallbackListings);
      setCurrentStep('results');
    } finally {
      // Don't reset state if aborted — the stop dialog handler will do it
      if (!enhancementAbortRef.current?.signal.aborted) {
        setIsGenerating(false);
        setGenerationPhase('complete');
        setProcessingPhotoId(undefined);
        setProcessedCount(0);
      }
      enhancementAbortRef.current = null;
    }
  };

  // Track if any photos were re-enhanced (for video regeneration prompt)
  const [photosReEnhanced, setPhotosReEnhanced] = useState(false);

  // Re-enhance a single photo
  const handleReEnhancePhoto = async (photoId: string) => {
    const photoIndex = photos.findIndex(p => p.id === photoId);
    if (photoIndex === -1) return;

    const photo = photos[photoIndex];
    setReEnhancingPhotoId(photoId);

    try {
      // Re-edits are the only quota-gated photo operation
      const quota = await checkReeditQuota(user?.id, { listingId: savedListingId, photoIndex });
      if (!quota.allowed) {
        toast({
          title: "Re-edit limit reached",
          description: quota.message || "You've used all re-edits included in your plan this month.",
          variant: "destructive",
        });
        setReEnhancingPhotoId(undefined);
        return;
      }

      const photoToEnhance = {
        ...photo,
        enhanced: undefined,
      };

      const result = await enhancePhotosWithAI([photoToEnhance], undefined, profile);
      
      if (result.photos.length > 0 && result.photos[0].enhanced) {
        await consumeReeditQuota(user?.id);
        const updatedPhotos = [...photos];
        const originalEnhanced = updatedPhotos[photoIndex].enhanced;
        updatedPhotos[photoIndex] = {
          ...updatedPhotos[photoIndex],
          reEnhanced: result.photos[0].enhanced, // Store re-enhanced separately
          previousEnhanced: originalEnhanced, // Keep for backward compat
          enhanced: result.photos[0].enhanced, // Show re-enhanced by default
          activeVersion: 'reenhanced',
        };
        setPhotos(updatedPhotos);
        setPhotosReEnhanced(true);

        if (savedListingId && user) {
          const enhancedPhotoUrls = updatedPhotos.map(p => p.enhanced || p.preview);
          const photoEnhancementOptions = updatedPhotos.map(p => p.enhancementOptions || { addDecorations: false, goldenHourMode: false });
          await supabase.from('listings').update({
            enhanced_photos: enhancedPhotoUrls,
            photo_enhancement_options: photoEnhancementOptions,
          } as any).eq('id', savedListingId);
        }

        toast({
          title: "Photo re-enhanced!",
          description: "Don't like it? Use the dropdown on the photo to revert.",
        });
        setReEnhancedPhotoIds(prev => new Set(prev).add(photoId));
      } else {
        toast({
          title: "Enhancement failed",
          description: "Could not re-enhance the photo. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Re-enhancement failed:', error);
      toast({
        title: "Enhancement failed",
        description: error instanceof Error ? error.message : "Could not re-enhance the photo.",
        variant: "destructive",
      });
    } finally {
      setReEnhancingPhotoId(undefined);
    }
  };

  // Switch a photo between versions: reenhanced, enhanced (first), or original
  const handleRevertPhoto = async (photoId: string, target: 'reenhanced' | 'enhanced' | 'original') => {
    const photoIndex = photos.findIndex(p => p.id === photoId);
    if (photoIndex === -1) return;

    const photo = photos[photoIndex];
    const updatedPhotos = [...photos];
    
    let newEnhanced: string | undefined;
    if (target === 'reenhanced' && photo.reEnhanced) {
      newEnhanced = photo.reEnhanced;
    } else if (target === 'enhanced' && photo.previousEnhanced) {
      newEnhanced = photo.previousEnhanced;
    } else if (target === 'original') {
      newEnhanced = photo.preview;
    } else {
      return;
    }

    updatedPhotos[photoIndex] = {
      ...updatedPhotos[photoIndex],
      enhanced: newEnhanced,
      activeVersion: target,
    };

    setPhotos(updatedPhotos);
    setPhotosReEnhanced(true);

    // Update saved listing
    if (savedListingId && user) {
      const enhancedPhotoUrls = updatedPhotos.map(p => p.enhanced || p.preview);
      await supabase.from('listings').update({
        enhanced_photos: enhancedPhotoUrls,
      } as any).eq('id', savedListingId);
    }

    const labels = { reenhanced: 'Re-enhanced', enhanced: 'First enhanced', original: 'Original' };
    toast({
      title: `Switched to ${labels[target]}`,
      description: "Photo has been updated.",
    });
  };

  // Regenerate video with current photos
  const handleRegenerateVideo = async () => {
    const enhancedPhotoUrls = photos
      .filter(p => p.enhanced)
      .map(p => ({
        url: p.enhanced!,
        roomType: p.roomType || 'other' as const,
      }));

    if (enhancedPhotoUrls.length === 0) return;

    setMediaGenerationStatus(prev => ({ ...prev, video: 'generating' }));
    
    try {
      const result = await generatePropertyVideo(enhancedPhotoUrls, formData.type, formData.city);
      if (result.success && result.videoUrl) {
        const newVideoBlob = result.blob;
        setVideoUrl(result.videoUrl);
        setMediaBlobs(prev => ({ ...prev, video: result.blob }));
        setMediaGenerationStatus(prev => ({ ...prev, video: 'complete' }));
        setPhotosReEnhanced(false);

        // Upload new video to storage
        if (savedListingId && user && newVideoBlob) {
          const videoStorageUrl = await uploadMediaToStorage(newVideoBlob, user.id, savedListingId, 'video');
          if (videoStorageUrl) {
            setVideoUrl(videoStorageUrl);
            await supabase.from('listings').update({ video_url: videoStorageUrl }).eq('id', savedListingId);
          }
        }

        toast({ title: "Video regenerated!", description: "Your slideshow has been updated with the latest photos." });
      } else {
        setMediaGenerationStatus(prev => ({ ...prev, video: 'error' }));
        toast({ title: "Video generation failed", description: "Could not regenerate the video.", variant: "destructive" });
      }
    } catch (error) {
      console.error('Video regeneration failed:', error);
      setMediaGenerationStatus(prev => ({ ...prev, video: 'error' }));
      toast({ title: "Video generation failed", variant: "destructive" });
    }
  };

  // ─── Cinematic AI Video add-on ───────────────────────────────────────────────

  // Refresh listing-level cinematic add-on state from DB
  const refreshCinematicState = useCallback(async (listingId: string) => {
    const { data, error } = await supabase
      .from('listings')
      .select('video_addon_purchased, video_regenerations_used, video_regenerations_paid, is_trial, video_resolution, video_clip_regens_used, video_clip_regens_allowance')
      .eq('id', listingId)
      .maybeSingle();
    if (error || !data) return;
    setCinematicAddon({
      purchased: !!data.video_addon_purchased,
      used: data.video_regenerations_used ?? 0,
      paid: data.video_regenerations_paid ?? 0,
      isTrial: !!data.is_trial,
      resolution: data.video_resolution === '1080p' ? '1080p' : '720p',
      clipRegensUsed: data.video_clip_regens_used ?? 0,
      clipRegensAllowance: data.video_clip_regens_allowance ?? 4,
    });
    if (data.video_addon_purchased) {
      const clips = await loadListingClips(listingId);
      if (clips.length > 0) setCinematicClips(clips);
    }
  }, []);

  // Whenever a listing is saved, fetch its add-on state.
  useEffect(() => {
    if (savedListingId) {
      refreshCinematicState(savedListingId);
    } else {
      setCinematicAddon({ purchased: false, used: 0, paid: 0, isTrial: false, resolution: '720p', clipRegensUsed: 0, clipRegensAllowance: 4 });
      setCinematicClips([]);
    }
  }, [savedListingId, refreshCinematicState]);

  // If the user pre-paid for Cinematic AI Video during creation (flag set in
  // UnifiedListingChat once the Stripe payment was confirmed and a pending
  // credit landed on the profile), consume that credit for this listing and
  // kick off generation automatically.
  useEffect(() => {
    if (!savedListingId) return;
    let pending = false;
    try { pending = sessionStorage.getItem('pendingCinematicPurchase') === '1'; } catch { /* ignore */ }
    if (!pending) return;
    try { sessionStorage.removeItem('pendingCinematicPurchase'); } catch { /* ignore */ }
    (async () => {
      try {
        const { data, error } = await supabase.rpc('consume_pending_cinematic_credit', {
          p_listing_id: savedListingId,
        });
        const row = Array.isArray(data) ? data[0] : data;
        if (error || !row?.success) {
          toast({
            title: 'Cinematic video not activated',
            description: row?.error_message || error?.message || 'Please try again from the results page.',
            variant: 'destructive',
          });
          return;
        }
        await refreshCinematicState(savedListingId);
        await runCinematicGeneration(savedListingId);
      } catch (e) {
        console.error('Consume cinematic credit failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedListingId]);

  // Run the actual cinematic generation (assumes entitlement already granted)
  const runCinematicGeneration = useCallback(async (listingId: string) => {
    const enhancedPhotos = photos
      .filter((p) => p.enhanced)
      .map((p) => ({ url: p.enhanced!, roomType: p.roomType || ('other' as const) }));
    if (enhancedPhotos.length === 0) {
      toast({ title: 'No photos to use', description: 'Enhance at least one photo first.', variant: 'destructive' });
      return;
    }

    setIsGeneratingCinematic(true);
    setMediaGenerationStatus((prev) => ({ ...prev, video: 'generating' }));
    setCinematicProgress({ done: 0, total: Math.min(8, enhancedPhotos.length), stage: 'clips' });

    try {
      const result = await generateCinematicPropertyVideo(
        enhancedPhotos,
        listingId,
        (info) => {
          setCinematicProgress({ done: info.clipsDone, total: info.clipsTotal, stage: info.stage });
          if (info.clips) setCinematicClips(info.clips);
        },
        cinematicStyle
      );

      if (result.clips) setCinematicClips(result.clips);

      if (!result.success || !result.videoUrl) {
        throw new Error(result.error || 'Cinematic video generation failed');
      }

      setVideoUrl(result.videoUrl);
      setMediaBlobs((prev) => ({ ...prev, video: result.blob }));
      setMediaGenerationStatus((prev) => ({ ...prev, video: 'complete' }));
      setPhotosReEnhanced(false);

      // Increment used counter in DB and upload final video.
      const newUsed = (cinematicAddon.used ?? 0) + 1;
      const updates: Record<string, unknown> = { video_regenerations_used: newUsed };
      updates.video_style = cinematicStyle;

      if (user && result.blob) {
        const storageUrl = await uploadMediaToStorage(result.blob, user.id, listingId, 'video');
        if (storageUrl) {
          setVideoUrl(storageUrl);
          updates.video_url = storageUrl;
        }
      }

      await supabase.from('listings').update(updates as never).eq('id', listingId);
      await refreshCinematicState(listingId);

      toast({
        title: 'Cinematic video ready',
        description: 'Your AI-generated property video is live.',
      });
    } catch (e) {
      console.error('Cinematic generation failed:', e);
      setMediaGenerationStatus((prev) => ({ ...prev, video: 'error' }));
      toast({
        title: 'Generation failed',
        description: e instanceof Error ? e.message : 'Could not generate cinematic video.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingCinematic(false);
      setCinematicProgress({ done: 0, total: 0, stage: 'idle' });
    }
  }, [photos, user, cinematicAddon.used, cinematicStyle, refreshCinematicState]);

  // Buy initial cinematic add-on, an extra paid regeneration, or a 1080p upgrade
  const handlePurchaseCinematic = useCallback(async (
    purpose: 'initial' | 'regeneration' | 'resolution_upgrade',
    resolution: '720p' | '1080p' = '720p'
  ) => {
    if (!savedListingId) {
      toast({ title: 'Save your listing first', description: 'Please save the listing before purchasing the add-on.', variant: 'destructive' });
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('create-cinematic-video-checkout', {
        body: { listingId: savedListingId, purpose, resolution, ...getDataFastAttribution() },
      });
      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (e) {
      console.error('Cinematic checkout failed:', e);
      toast({
        title: 'Checkout failed',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  }, [savedListingId]);

  // Generate (or free-regenerate) the cinematic video. Triggered from ListingResults.
  const handleGenerateCinematic = useCallback(async (resolution: '720p' | '1080p' = '720p') => {
    if (!savedListingId) return;
    if (!cinematicAddon.purchased) {
      await handlePurchaseCinematic('initial', resolution);
      return;
    }
    // Entitlement: used <= paid + 1
    if (cinematicAddon.used > cinematicAddon.paid) {
      // Used the free regen → need to buy another
      await handlePurchaseCinematic('regeneration');
      return;
    }
    await runCinematicGeneration(savedListingId);
  }, [savedListingId, cinematicAddon, handlePurchaseCinematic, runCinematicGeneration]);

  // Upgrade an already-purchased listing from 720p to 1080p ($5)
  const handleUpgradeCinematicResolution = useCallback(async () => {
    await handlePurchaseCinematic('resolution_upgrade', '1080p');
  }, [handlePurchaseCinematic]);

  // Re-stitch the current set of ready clips into one MP4 and persist it.
  const restitchCinematicVideo = useCallback(async (listingId: string, clips: CinematicClip[]) => {
    const readyUrls = clips.filter((c) => c.status === 'ready' && c.url).map((c) => c.url!);
    if (readyUrls.length === 0) return;
    setCinematicProgress({ done: readyUrls.length, total: readyUrls.length, stage: 'stitching' });
    try {
      const { blob, objectUrl } = await stitchClipsToMp4(readyUrls);
      setVideoUrl(objectUrl);
      setMediaBlobs((prev) => ({ ...prev, video: blob }));
      if (user) {
        const storageUrl = await uploadMediaToStorage(blob, user.id, listingId, 'video');
        if (storageUrl) {
          setVideoUrl(storageUrl);
          await supabase.from('listings').update({ video_url: storageUrl } as never).eq('id', listingId);
        }
      }
    } finally {
      setCinematicProgress({ done: 0, total: 0, stage: 'idle' });
    }
  }, [user]);

  // Regenerate a single clip, then re-stitch the video.
  const handleRegenerateClip = useCallback(async (clipIndex: number) => {
    if (!savedListingId) return;
    const clip = cinematicClips.find((c) => c.clipIndex === clipIndex);
    if (!clip) return;

    setRegeneratingClipIndex(clipIndex);
    try {
      const { url, storagePath } = await generateSingleClip({
        imageUrl: clip.sourcePhotoUrl,
        photoIndex: clip.photoIndex,
        clipIndex: clip.clipIndex,
        listingId: savedListingId,
        roomType: clip.roomType,
        style: cinematicStyle,
        isRegeneration: true,
      });
      const next = cinematicClips.map((c) =>
        c.clipIndex === clipIndex ? { ...c, status: 'ready' as const, url, storagePath, error: undefined } : c
      );
      setCinematicClips(next);
      await refreshCinematicState(savedListingId);
      // Re-load clips so stitching always uses fresh signed URLs (they expire after 1h)
      let stitchClips = next;
      try {
        const fresh = await loadListingClips(savedListingId);
        if (fresh.length > 0) {
          stitchClips = fresh;
          setCinematicClips(fresh);
        }
      } catch {
        // fall back to local clips
      }
      await restitchCinematicVideo(savedListingId, stitchClips);
      toast({ title: 'Clip regenerated', description: 'Your video has been rebuilt with the new shot.' });
    } catch (e) {
      toast({
        title: 'Clip regeneration failed',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingClipIndex(null);
    }
  }, [savedListingId, cinematicClips, cinematicStyle, refreshCinematicState, restitchCinematicVideo]);

  // Handle Stripe success redirect: auto-run generation after payment.
  useEffect(() => {
    const status = searchParams.get('cinematic_video');
    const lid = searchParams.get('listing_id');
    if (!status || !lid) return;

    if (status === 'success') {
      (async () => {
        toast({ title: 'Payment successful', description: 'Preparing your cinematic video…' });
        // Webhook should have flipped video_addon_purchased / incremented paid by now,
        // but allow a brief delay + a few refreshes in case of latency.
        for (let i = 0; i < 5; i++) {
          await refreshCinematicState(lid);
          const { data } = await supabase
            .from('listings')
            .select('video_addon_purchased, video_regenerations_paid, video_regenerations_used')
            .eq('id', lid)
            .maybeSingle();
          if (data?.video_addon_purchased && (data.video_regenerations_used ?? 0) <= (data.video_regenerations_paid ?? 0) + 1) {
            break;
          }
          await new Promise((r) => setTimeout(r, 1500));
        }
        setSavedListingId(lid);
        await runCinematicGeneration(lid);
        setSearchParams({}, { replace: true });
      })();
    } else if (status === 'canceled') {
      toast({ title: 'Checkout canceled', description: 'No charge was made.' });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSaveListing = async (editedListings: ListingsByLanguage[]) => {
    if (!user) {
      toast({
        title: "Login required",
        description: "Please login to save your listings.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    
    // Get first language's listings for primary columns (backward compat)
    const primaryListings = editedListings[0]?.listings || [];
    const facebookListing = primaryListings.find(l => l.platform === 'facebook');
    const generalListing = primaryListings.find(l => l.platform === 'general');
    const instagramListing = primaryListings.find(l => l.platform === 'instagram');
    const instagramStory = primaryListings.find(l => l.platform === 'instagram-story');

    // Generate a temporary listing ID for photo uploads
    const tempListingId = crypto.randomUUID();

    // Upload all photos to storage BEFORE saving to database
    // This ensures we store permanent URLs, not blob URLs
    console.log('📤 Uploading photos to storage before saving...');
    const { uploadAllPhotosToStorage } = await import('@/lib/uploadMedia');
    const { originalUrls, enhancedUrls } = await uploadAllPhotosToStorage(
      photos.map(p => ({ preview: p.preview, enhanced: p.enhanced })),
      user.id,
      tempListingId,
      (current, total) => {
        console.log(`📸 Uploading photo ${current}/${total}`);
      }
    );
    console.log('✅ Photos uploaded to storage');

    const { data, error } = await supabase.from('listings').insert({
      user_id: user.id,
      listing_type: formData.listingType,
      property_type: formData.type,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      square_meters: formData.squareMeters ? parseInt(formData.squareMeters) : null,
      price: parseInt(formData.price),
      address: formData.address,
      city: formData.city,
      features: formData.features,
      condition: formData.condition,
      highlights: formData.highlights,
      language: formData.languages.join(','),
      // Use the uploaded storage URLs instead of blob URLs
      photos: originalUrls,
      enhanced_photos: enhancedUrls,
      // Store per-photo enhancement options for re-enhancement
      photo_enhancement_options: photos.map(p => p.enhancementOptions || { addDecorations: false, goldenHourMode: false }),
      facebook_title: facebookListing?.title,
      facebook_description: facebookListing?.description,
      general_title: generalListing?.title,
      general_description: generalListing?.description,
      instagram_post: instagramListing?.description,
      instagram_story: instagramStory?.description,
      // Store all languages content as JSONB
      content_by_language: JSON.parse(JSON.stringify(editedListings)),
      is_trial: shouldMarkListingAsTrial(billing),
      profile_picture_url: profilePictureUrl || null,
      video_url: videoUrl || null,
    } as any).select().single();

    if (error) {
      console.error('Error saving listing:', error);
      toast({
        title: "Error saving",
        description: error.message,
        variant: "destructive",
      });
    } else {
      // Store the listing ID for QR code generation
      if (data?.id) {
        setSavedListingId(data.id);
      }
      
      toast({
        title: "Listing saved!",
        description: `Saved in ${editedListings.length} language${editedListings.length > 1 ? 's' : ''}. View in My Listings.`,
      });

      // Create and upload thumbnail from first photo
      if (data?.id && photos.length > 0) {
        try {
          const { createAndUploadThumbnail } = await import('@/lib/createThumbnail');
          const firstPhoto = photos[0];
          const imageSource = firstPhoto.enhanced || firstPhoto.preview;
          
          if (imageSource && !imageSource.startsWith('blob:')) {
            const thumbnailUrl = await createAndUploadThumbnail(imageSource, data.id, user.id);
            if (thumbnailUrl) {
              await supabase.from('listings').update({ thumbnail_url: thumbnailUrl }).eq('id', data.id);
            }
          } else if (firstPhoto.file) {
            // Use original file if available
            const thumbnailUrl = await createAndUploadThumbnail(firstPhoto.file, data.id, user.id);
            if (thumbnailUrl) {
              await supabase.from('listings').update({ thumbnail_url: thumbnailUrl }).eq('id', data.id);
            }
          }
        } catch (thumbError) {
          console.error('Failed to create thumbnail:', thumbError);
        }
      }

      // Always send in-app notification; only include email if user opted in
      if (data?.id && user.id) {
        try {
          const listingTitle = generalListing?.title || facebookListing?.title || `${formData.type} in ${formData.city}`;
          await supabase.functions.invoke('send-notification', {
            body: {
              to: sendEmailOnComplete ? user.email : undefined,
              userId: user.id,
              listingId: data.id,
              listingTitle,
              status: 'complete',
            },
          });
        } catch (notifyError) {
          console.error('Failed to send notification:', notifyError);
        }
      }
    }
    
    setIsSaving(false);
  };

  const handleStartOver = () => {
    setFormData(initialFormData);
    setPhotos([]);
    setListingsByLanguage([]);
    setSavedListingId(null);
    setProfilePictureUrl(null);
    setVideoUrl(null);
    setMediaGenerationStatus({ profilePicture: 'idle', video: 'idle' });
    setCurrentStep('create'); // Go directly to create step, not hero
  };

  const handleGetStarted = () => {
    if (!user) {
      toast({
        title: "Login required",
        description: "Please login or create an account to create listings.",
      });
      navigate('/auth');
      return;
    }
    const demoPhotos = consumeDemoPhotos();
    if (demoPhotos.length > 0) {
      setPhotos(demoPhotos);
      toast({
        title: "Photo added to your listing",
        description: "Your unlocked photo is loaded and already enhanced — add more photos or continue.",
      });
    }
    setCurrentStep('create');
  };

  const homeJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'ListBright',
    url: 'https://listbright.ai',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: 'AI-powered real estate listing generator with photo enhancement and multi-language descriptions.',
    offers: { '@type': 'AggregateOffer', lowPrice: '0', highPrice: '219', priceCurrency: 'USD' },
  }), []);

  if (currentStep === 'hero') {
    return (
      <Layout>
        <PageMeta
          title="Relaunch Listings That Aren't Selling | ListBright"
          description="For listing agents and brokers: rebuild photos, staging, copy, video and a microsite after a price cut or relist — in about 10 minutes, straight from your phone, no reshoot."
          jsonLd={homeJsonLd}
        />
        <EnhancedHeroSection onGetStarted={handleGetStarted} />
        {user && <WelcomeTour />}
      </Layout>
    );
  }

  return (
    <div className="min-h-screen gradient-warm">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setCurrentStep('hero')}
              className="flex items-center gap-2 font-display font-semibold text-lg hover:text-primary transition-colors"
            >
              <img src="/logo.png" alt="ListBright logo" width={1080} height={589} className="h-16 md:h-20 w-auto" decoding="async" />
            </button>
            <div className="flex items-center gap-3">
              {user && (
                <div className="flex items-center gap-2">
                  <NotificationBell />
                  <Button variant="ghost" size="sm" onClick={() => navigate('/my-listings')} title="My Listings" aria-label="My Listings">
                    <LayoutList className="w-4 h-4" />
                    <span className="sr-only">My Listings</span>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} title="Profile" aria-label="Profile">
                    <User className="w-4 h-4" />
                    <span className="sr-only">Profile</span>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={signOut} title="Log out" aria-label="Log out">
                    <LogOut className="w-4 h-4" />
                    <span className="sr-only">Log out</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4 max-w-2xl">
        {/* Show upgrade banner when near or at limit */}
        {user && billing && tierConfig && tierConfig.activeListingsLimit !== null && tierConfig.activeListingsLimit > 0 && (
          <UpgradeBanner
            currentTier={billing.subscriptionTier}
            limitType="listings"
            currentCount={billing.activeListingsCount}
            limit={tierConfig.activeListingsLimit}
            className="mb-4"
          />
        )}

        {currentStep === 'create' && (
          <UnifiedListingChat
            data={formData}
            onChange={setFormData}
            photos={photos}
            onPhotosChange={setPhotos}
            onGenerate={generateListings}
            onStopEnhancement={handleStopEnhancement}
            isGenerating={isGenerating}
            processingPhotoId={processingPhotoId}
            processedCount={processedCount}
            userEmail={user?.email}
            generationPhase={generationPhase}
            mediaGenerationStatus={mediaGenerationStatus}
          />
        )}

        {currentStep === 'results' && (
          <>
          {savedListingId && (
            <div className="mb-6">
              <ListingScoreCard listingId={savedListingId} />
            </div>
          )}
          <ListingResults
            formData={formData}
            photos={photos}
            listingsByLanguage={listingsByLanguage}
            profile={profile}
            listingId={savedListingId || undefined}
            profilePictureUrl={profilePictureUrl}
            videoUrl={videoUrl}
            onRegenerate={generateListings}
            onStartOver={handleStartOver}
            onReEnhancePhoto={handleReEnhancePhoto}
            onRevertPhoto={handleRevertPhoto}
            onRegenerateVideo={videoUrl ? handleRegenerateVideo : undefined}
            isRegenerating={isGenerating}
            isSaving={isSaving}
            reEnhancingPhotoId={reEnhancingPhotoId}
            reEnhancedPhotoIds={reEnhancedPhotoIds}
            photosReEnhanced={photosReEnhanced}
            isRegeneratingVideo={mediaGenerationStatus.video === 'generating'}
            cinematicAddon={cinematicAddon}
            cinematicProgress={cinematicProgress}
            isGeneratingCinematic={isGeneratingCinematic}
            onGenerateCinematic={handleGenerateCinematic}
            cinematicStyle={cinematicStyle}
            onCinematicStyleChange={setCinematicStyle}
            cinematicClips={cinematicClips}
            regeneratingClipIndex={regeneratingClipIndex}
            onRegenerateClip={handleRegenerateClip}
            onUpgradeCinematicResolution={handleUpgradeCinematicResolution}
          />
          </>
        )}
      </main>

      {/* Upgrade Prompt Dialog */}
      {billing && (
        <UpgradePrompt
          isOpen={showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(false)}
          currentTier={billing.subscriptionTier}
          limitType={upgradeLimitType}
          currentCount={
            upgradeLimitType === 'listings' 
              ? billing.activeListingsCount 
              : upgradeLimitType === 'photoEdits'
                ? (billing.monthlyPhotoEditsUsed || 0)
                : upgradeLimitType === 'virtualStaging'
                  ? (billing.monthlyStagingPropertiesUsed || 0)
                  : (billing.monthlyAiAdditionsUsed || 0)
          }
          limit={
            upgradeLimitType === 'listings'
              ? (tierConfig?.activeListingsLimit || 0)
              : upgradeLimitType === 'photoEdits'
                ? (tierConfig?.monthlyLimits.photoEdits || 0)
                : upgradeLimitType === 'virtualStaging'
                  ? (tierConfig?.monthlyLimits.virtualStagingProperties || 0)
                  : (tierConfig?.monthlyLimits.aiAdditions || 0)
          }
        />
      )}

      {/* Stop Enhancement Confirmation Dialog */}
      <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enhancement stopped</AlertDialogTitle>
            <AlertDialogDescription>
              {processedCount > 0 
                ? `${processedCount} of ${photos.length} photo${photos.length !== 1 ? 's' : ''} were enhanced before stopping. What would you like to do with the results?`
                : 'Enhancement was stopped before any photos were processed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            {processedCount > 0 && (
              <AlertDialogAction
                onClick={() => handleStopDialogChoice(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Keep {processedCount} enhanced photo{processedCount !== 1 ? 's' : ''}
              </AlertDialogAction>
            )}
            <AlertDialogAction
              onClick={() => handleStopDialogChoice(false)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard all, use originals
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
